/**
 * VisualLayer.ts — Orchestrator for all visual effects in Gallowcrest.
 *
 * Manages CRT shader, particle system, and FPS monitoring with
 * auto-quality scaling when performance drops.
 *
 * Location-to-particle mapping for Gallowcrest's 13 locations:
 * - snow: citadel, frostpeak, frozen_wastes, mountain_pass, title/menu/default
 * - embers: dragons_lair, the_forge, combat
 * - arcane: ancient_library, temple_of_dawn, arcane_sanctum
 * - mist: shadow_keep, catacombs, crystal_caves
 */

import { CRTEffect } from './CRTEffect';
import { ParticleSystem, type ParticlePreset } from './ParticleSystem';
import type { SettingsManager } from '@game/Settings';
import type { NotificationManager } from './AchievementNotifier';

// ─── Location → Particle Mapping ─────────────────────────────────

const LOCATION_PARTICLE_MAP: Record<string, ParticlePreset> = {
    // Snow locations
    gallowcrest_citadel: 'snow',
    frostpeak: 'snow',
    frozen_wastes: 'snow',
    mountain_pass: 'snow',
    // Fire/ember locations
    dragons_lair: 'embers',
    the_forge: 'embers',
    // Arcane locations
    ancient_library: 'arcane',
    temple_of_dawn: 'arcane',
    arcane_sanctum: 'arcane',
    // Mist/shadow locations
    shadow_keep: 'mist',
    catacombs: 'mist',
    crystal_caves: 'mist',
    // Default
    marketplace: 'snow',
};

// ─── FPS Monitor ────────────────────────────────────────────────

const FPS_SAMPLE_SIZE = 60;
const FPS_CHECK_INTERVAL = 2000;

/** Quality tiers for auto-scaling. */
export type QualityTier = 'full' | 'reduced' | 'minimal' | 'off';

interface FPSMonitor {
    samples: number[];
    index: number;
    lastTime: number;
    average: number;
}

// ─── VisualLayer ────────────────────────────────────────────────

export class VisualLayer {
    readonly container: HTMLElement;
    private crt: CRTEffect;
    private particles: ParticleSystem;
    private settings: SettingsManager;
    private notifier: NotificationManager | null = null;

    private fpsMonitor: FPSMonitor;
    private qualityTier: QualityTier = 'full';
    private autoScale = true;
    private qualityCheckTimer = 0;
    private rafId = 0;
    private monitoring = false;

    constructor(
        container: HTMLElement,
        crt: CRTEffect,
        particles: ParticleSystem,
        settings: SettingsManager,
    ) {
        this.container = container;
        this.crt = crt;
        this.particles = particles;
        this.settings = settings;

        this.fpsMonitor = {
            samples: new Array(FPS_SAMPLE_SIZE).fill(60),
            index: 0,
            lastTime: 0,
            average: 60,
        };
    }

    // ─── Public API ─────────────────────────────────────────────

    /** Apply saved settings on boot. */
    applySettings(): void {
        const s = this.settings.get();
        if (s.crtEffect) this.crt.enable(); else this.crt.disable();
        if (s.particles) this.particles.enable(); else this.particles.disable();
        if (s.particlePreset) {
            this.particles.setPreset(s.particlePreset as ParticlePreset);
        }
    }

    /** Set the notification manager for update loop. */
    setNotifier(notifier: NotificationManager): void {
        this.notifier = notifier;
    }

    /** Enable CRT effects and persist. */
    enableCRT(): void {
        this.crt.enable();
        this.settings.update({ crtEffect: true });
    }

    /** Disable CRT effects and persist. */
    disableCRT(): void {
        this.crt.disable();
        this.settings.update({ crtEffect: false });
    }

    /** Toggle CRT effects. Returns new state. */
    toggleCRT(): boolean {
        if (this.crt.isEnabled()) {
            this.disableCRT();
            return false;
        }
        this.enableCRT();
        return true;
    }

    /** Enable particle system and persist. */
    enableParticles(): void {
        this.particles.enable();
        this.settings.update({ particles: true });
    }

    /** Disable particle system and persist. */
    disableParticles(): void {
        this.particles.disable();
        this.settings.update({ particles: false });
    }

    /** Toggle particle system. Returns new state. */
    toggleParticles(): boolean {
        if (this.particles.isEnabled()) {
            this.disableParticles();
            return false;
        }
        this.enableParticles();
        return true;
    }

    /** Set particle preset and persist. */
    setParticlePreset(preset: ParticlePreset): void {
        this.particles.setPreset(preset);
        this.settings.update({ particlePreset: preset });
    }

    /** Enable all visual effects. */
    enableAll(): void {
        this.enableCRT();
        this.enableParticles();
    }

    /** Disable all visual effects. */
    disableAll(): void {
        this.disableCRT();
        this.disableParticles();
    }

    /** Get the CRT subsystem. */
    getCRT(): CRTEffect { return this.crt; }

    /** Get the particle subsystem. */
    getParticles(): ParticleSystem { return this.particles; }

    // ─── Scene Triggers ─────────────────────────────────────────

    private savedPreset: ParticlePreset | null = null;

    /**
     * Set particle preset based on a Gallowcrest location ID.
     * Does not persist — this is a transient scene override.
     */
    setLocationParticles(locationId: string): void {
        if (!this.settings.get().particles) return;
        const preset = LOCATION_PARTICLE_MAP[locationId] ?? 'snow';
        this.particles.setPreset(preset);
    }

    /**
     * Switch to combat particles (embers).
     * Saves the current preset so it can be restored after combat.
     */
    enterCombat(): void {
        if (!this.settings.get().particles) return;
        this.savedPreset = this.particles.getPreset();
        this.particles.setPreset('embers');
    }

    /**
     * Restore the particle preset saved before combat.
     */
    exitCombat(): void {
        if (!this.settings.get().particles) return;
        if (this.savedPreset) {
            this.particles.setPreset(this.savedPreset);
            this.savedPreset = null;
        } else {
            this.particles.setPreset(this.settings.get().particlePreset as ParticlePreset ?? 'snow');
        }
    }

    /**
     * Reset particles to the player's saved ambient preset.
     */
    resetToAmbient(): void {
        this.savedPreset = null;
        this.particles.setPreset(this.settings.get().particlePreset as ParticlePreset ?? 'snow');
    }

    // ─── FPS Monitoring + Auto-Quality ──────────────────────────

    /** Start FPS monitoring. Call once after boot. */
    startMonitoring(): void {
        if (this.monitoring) return;
        this.monitoring = true;
        this.fpsMonitor.lastTime = performance.now();
        this.tick(performance.now());
        this.qualityCheckTimer = window.setInterval(
            () => this.checkQuality(),
            FPS_CHECK_INTERVAL,
        );
    }

    /** Stop FPS monitoring. */
    stopMonitoring(): void {
        if (!this.monitoring) return;
        this.monitoring = false;
        if (this.rafId) {
            cancelAnimationFrame(this.rafId);
            this.rafId = 0;
        }
        if (this.qualityCheckTimer) {
            clearInterval(this.qualityCheckTimer);
            this.qualityCheckTimer = 0;
        }
    }

    /** Get current average FPS. */
    getFPS(): number {
        return this.fpsMonitor.average;
    }

    /** Get current quality tier. */
    getQualityTier(): QualityTier {
        return this.qualityTier;
    }

    /** Enable or disable auto-quality scaling. */
    setAutoScale(enabled: boolean): void {
        this.autoScale = enabled;
        if (!enabled) {
            this.qualityTier = 'full';
        }
    }

    /** Check if reduced-motion is preferred by the OS. */
    prefersReducedMotion(): boolean {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    // ─── Cleanup ────────────────────────────────────────────────

    /** Tear down all visual subsystems. */
    dispose(): void {
        this.stopMonitoring();
        this.crt.disable();
        this.particles.disable();
    }

    // ─── Internal ───────────────────────────────────────────────

    private tick(now: number): void {
        if (!this.monitoring) return;

        const delta = now - this.fpsMonitor.lastTime;
        if (delta > 0) {
            const fps = 1000 / delta;
            const m = this.fpsMonitor;
            m.samples[m.index] = fps;
            m.index = (m.index + 1) % FPS_SAMPLE_SIZE;
            let sum = 0;
            for (let i = 0; i < FPS_SAMPLE_SIZE; i++) sum += m.samples[i];
            m.average = sum / FPS_SAMPLE_SIZE;
        }
        this.fpsMonitor.lastTime = now;

        // Update notification manager if set
        if (this.notifier) {
            this.notifier.update();
        }

        this.rafId = requestAnimationFrame((t) => this.tick(t));
    }

    private checkQuality(): void {
        if (!this.autoScale) return;

        const fps = this.fpsMonitor.average;
        const prev = this.qualityTier;

        if (fps < 15) {
            this.qualityTier = 'off';
        } else if (fps < 20) {
            this.qualityTier = 'minimal';
        } else if (fps < 30) {
            this.qualityTier = 'reduced';
        } else if (fps >= 45 && this.qualityTier !== 'full') {
            this.qualityTier = 'full';
        }

        if (prev === this.qualityTier) return;

        switch (this.qualityTier) {
            case 'off':
            case 'minimal':
                // Only disable particles for performance; keep CRT if user wants it
                // (CRT is CSS-only and cheap; particles are canvas RAF and expensive)
                this.particles.disable();
                break;
            case 'reduced':
                if (this.settings.get().crtEffect) this.crt.enable();
                this.particles.disable();
                break;
            case 'full':
                this.applySettings();
                break;
        }
    }
}
